import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
  useRecoilState,
  useRecoilValueLoadable,
  useRecoilRefresher_UNSTABLE,
  useSetRecoilState,
} from 'recoil';
import { Button, CustomCarousel, Modal, SvgIcon } from '@ui-elements';
import {
  CompareRangePopupModel,
  CompareRangePopupModelState,
  CompareRangePopupVariant,
} from '../../store';
import styles from './index.module.scss';
import _ from 'lodash';
import {
  colorGreen,
  desktopMin,
  mobileMax,
  tabletMax,
  tabletMin,
} from '~/common/utils';
import { useMediaQuery } from 'react-responsive';
import { ResponsiveType } from 'react-multi-carousel';
import ContentLoader from 'react-content-loader';
import { money } from 'common/utils/formatters';
import { getPriceByPostcodeQuery } from 'common/store/user-location-state';
import { GenericModal } from '~/common/components/generic-modal';
import { usePortals } from 'react-portal-hook';
import { DetailedPricing } from '~/pages/showroom/components/price-details/RepaymentDisclaimers';
import uuid from 'react-uuid';
import { CRMModelVariants } from '~/common/models';
import { setCompareRangeSelectedVariants } from '~/common/services/compare-range-modal-service';

interface ModalProps {
  closeModal: () => void;
}

const defaultBreakpoints: ResponsiveType = {
  desktop: {
    breakpoint: { max: 4000, min: desktopMin },
    items: 6,
  },
  tablet: {
    breakpoint: { max: tabletMax, min: tabletMin },
    items: 4.5,
  },
  mobile: {
    breakpoint: { max: mobileMax, min: 0 },
    items: 2.5,
  },
};

const defaultVariantBreakpoints: ResponsiveType = {
  desktop: {
    breakpoint: { max: 4000, min: desktopMin },
    items: 4,
  },
  tablet: {
    breakpoint: { max: tabletMax, min: tabletMin },
    items: 2.5,
  },
  mobile: {
    breakpoint: { max: mobileMax, min: 0 },
    items: 1.5,
  },
};

interface CompareRangeModalRDP {
  variantId?: string;
  modelName?: string;
  pimIdentifier?: string;
  rdpDisclaimerOverride?: string;
}

export const CompareRangeModalRDP = React.memo<CompareRangeModalRDP>(
  ({ variantId, modelName, pimIdentifier, rdpDisclaimerOverride }) => {
    const price = useRecoilValueLoadable(
      getPriceByPostcodeQuery({ id: variantId })
    );
    const refresh = useRecoilRefresher_UNSTABLE(
      getPriceByPostcodeQuery({ id: variantId })
    );

    const portalManager = usePortals();
    const showFinanceDisclaimer = (element: any, title: string) => {
      portalManager.open((portal) => (
        <GenericModal
          title={title}
          element={element}
          closeModal={portal.close}
        />
      ));
    };
    return (
      <div className={styles.crmPriceContainer}>
        <div>
          {price.state === 'loading' ? (
            <div className={styles.crmPrice}>
              <ContentLoader
                speed={0.3}
                width={150}
                height={10}
                viewBox="0 0 150 10"
                backgroundColor="#aebbd6"
                foregroundColor="#ecebeb"
              >
                <rect x="0" y="0" rx="3" ry="3" width="150" height="10" />
              </ContentLoader>
            </div>
          ) : price.state === 'hasError' ? (
            <div className={cn(styles.crmPrice, styles.crmPriceError)}>
              <p>Failed to load RDP in your area.</p>
              <p onClick={refresh}>Click here to reload</p>
            </div>
          ) : (
            <div
              className={styles.crmPrice}
              onClick={() => {
                showFinanceDisclaimer(
                  DetailedPricing(
                    modelName ?? '',
                    price.contents.rdp,
                    price.contents.mlp,
                    pimIdentifier ?? '',
                    rdpDisclaimerOverride
                  ),
                  ''
                );
              }}
            >
              <p>
                From {money(price.contents.rdp, 0)} RDP
                <sup>
                  <i className="icon icon--info" />
                </sup>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export const CompareRangeModalContent: React.FC<ModalProps> = ({
  closeModal,
}) => {
  const [compareRangePopupModels] = useRecoilState(CompareRangePopupModelState);
  const setModelsState = useSetRecoilState(CompareRangePopupModelState);
  const maxSelection = 6;
  const maxVariantImageWidth = 200;
  const maxImageWidth = 200;
  const [showCTAs, setShowCTAs] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const [variantBreakpoints, setVariantBreakpoints] = useState(
    defaultVariantBreakpoints
  );
  const [breakpoints, setBreakpoints] = useState(defaultBreakpoints);
  const [isTablet, setIsTablet] = useState(
    useMediaQuery(
      { maxDeviceWidth: tabletMax },
      undefined,

      (matches) => {
        setIsTablet(matches);
      }
    )
  );
  const [isMobile, setIsMobile] = useState(
    useMediaQuery(
      { maxDeviceWidth: mobileMax },
      undefined,

      (matches) => {
        setIsMobile(matches);
      }
    )
  );

  const adjustBreakpoints = useCallback(() => {
    const _width = ref.current?.clientWidth || 0;
    const _padding = 20;
    let _containerWidth = _width - _padding;
    if (_containerWidth > 0) {
      const _variantCount = _containerWidth / maxVariantImageWidth;
      const variantItems = _variantCount > 3.75 ? 3.75 : _variantCount;
      setVariantBreakpoints(
        isMobile
          ? {
              ...variantBreakpoints,
              mobile: { ...variantBreakpoints.mobile, items: variantItems },
            }
          : isTablet
          ? {
              ...variantBreakpoints,
              tablet: { ...variantBreakpoints.tablet, items: variantItems },
            }
          : {
              ...variantBreakpoints,
              desktop: {
                ...variantBreakpoints.desktop,
                items: Math.round(variantItems),
              },
            }
      );

      _containerWidth =
        _width >= desktopMin - 80 ? _containerWidth - 100 : _containerWidth - 2;

      const _count = _containerWidth / maxImageWidth;
      const items = _count > 6 ? 6 : _count;

      setBreakpoints(
        isMobile
          ? {
              ...breakpoints,
              mobile: { ...breakpoints.mobile, items },
            }
          : isTablet
          ? {
              ...breakpoints,
              tablet: { ...breakpoints.tablet, items },
            }
          : {
              ...breakpoints,
              desktop: { ...breakpoints.desktop, items: Math.round(items) },
            }
      );
    }
  }, []);

  useEffect(() => adjustBreakpoints(), [adjustBreakpoints, isMobile, isTablet]);

  const handleModelClick = (id?: string) => {
    const _models = [...compareRangePopupModels].map((m) => ({
      ...m,
      _active: m.id === id,
    }));
    setModelsState(_models);
  };

  const getSelectedVariantsCount = () =>
    compareRangePopupModels.reduce(
      (acc, m) => acc + m.variants.filter((v) => v._selected).length,
      0
    );

  const canAddVariant = () => getSelectedVariantsCount() < maxSelection;

  const handleVariantClick = (modelPimIdentifier, variantPimIdentifier) => {
    if (!modelPimIdentifier || !variantPimIdentifier) return;

    const _date = new Date();
    const _models = [...compareRangePopupModels].map((m) =>
      m.pimIdentifier === modelPimIdentifier
        ? {
            ...m,
            variants: m.variants.map((v) => {
              const newSelectedValue = !v._selected;
              return v.pimIdentifier === variantPimIdentifier
                ? {
                    ...v,
                    _selected: newSelectedValue,
                    _order: newSelectedValue
                      ? _date.getTime()
                      : Number.MAX_SAFE_INTEGER,
                  }
                : v;
            }),
          }
        : m
    );

    setModelsState(_models);
  };

  const showArrows = (
    arr: CompareRangePopupModel[] | CompareRangePopupVariant[],
    _breakpoints: ResponsiveType,
    compareCount?: number
  ) => {
    const _compareCount =
      compareCount ||
      _breakpoints[!isTablet ? (!isMobile ? 'desktop' : 'mobile') : 'tablet']
        .items;
    return arr.length > _compareCount;
  };

  const handleCompareNow = () => {
    const result: CRMModelVariants[] = [...compareRangePopupModels]
      .map((m) =>
        m.variants
          .filter((v) => v._selected)
          .map((v) => ({
            id: uuid(),
            modelPimIdentifier: v.modelPimIdentifier || '',
            variantPimIdentifier: v.pimIdentifier || '',
            _order: v._order || Number.MAX_VALUE,
          }))
      )
      .flat();

    const _data = [...compareRangePopupModels]
      .map((m) => m.variants.filter((v) => v._selected))
      .flat()
      .map((n) => n.name);

    window['pushDigitalData'](
      {
        event: '_formNavigate',
        form: {
          name: 'compare subaru range',
          stage: 'compare',
          details: {
            vehicleSelected: _data.map((n) => ({
              make: 'Subaru',
              model: n?.split(' ')[0],
              variant: n,
            })),
          },
        },
      },
      true
    );

    setCompareRangeSelectedVariants(result);
    window.location.href = `/compare-cars?returnUrl=${window.location.pathname}`;
    closeModal();
  };

  const handleClearAll = () => setShowCTAs(!showCTAs);

  const handleConfirmClearAll = () => {
    const _models = [...compareRangePopupModels].map((m) => ({
      ...m,
      variants: [...m.variants].map((v) => ({
        ...v,
        _selected: false,
        _order: Number.MAX_SAFE_INTEGER,
      })),
    }));
    setModelsState(_models);
    setShowCTAs(!showCTAs);
  };

  useEffect(() => {
    window['pushDigitalData'](
      {
        event: '_formNavigate',
        form: {
          name: 'compare subaru range',
          stage: 'start',
        },
      },
      true
    );
  }, []);

  const renderModels = () => (
    <div className={styles.crmCarouselContainer}>
      <CustomCarousel
        className={cn(styles.crmCarousel, {
          [styles.crmCarouselCentered]: !showArrows(
            compareRangePopupModels,
            breakpoints
          ),
        })}
        responsive={breakpoints}
        partialVisible={
          (isTablet || isMobile) &&
          showArrows(compareRangePopupModels, breakpoints, 1)
        }
        removeArrowOnDeviceType={['mobile', 'tablet']}
        swipeable={isTablet || isMobile}
        arrows={
          !(isTablet || isMobile) &&
          showArrows(compareRangePopupModels, breakpoints)
        }
      >
        {compareRangePopupModels.map((m) => (
          <div
            key={m.id}
            className={cn(styles.crmModel, {
              [styles.crmModelActive]: m._active,
              [styles.crmClickable]: !m._active,
            })}
          >
            <div
              className={styles.crmClickableArea}
              onClick={() => {
                if (!m._active) handleModelClick(m.id);
              }}
            >
              <div className={styles.crmImage}>
                <img
                  src={m.imageSrc}
                  style={{
                    maxWidth: `${maxImageWidth}px`,
                  }}
                  loading="lazy"
                />
              </div>
              <div className={styles.crmDetails}>
                <p className={cn(styles.crmName, styles.crmUpperCase)}>
                  {m.name}
                </p>
              </div>
            </div>
          </div>
        ))}
      </CustomCarousel>
    </div>
  );

  const renderVariants = () => {
    const activeModelIdx = compareRangePopupModels.findIndex((m) => m._active);
    if (activeModelIdx < 0) return <></>;
    const variants = compareRangePopupModels[activeModelIdx].variants;

    return (
      <div className={styles.crmCarouselContainer}>
        <CustomCarousel
          className={cn(styles.crmCarousel, styles.crmCarouselDivider, {
            [styles.crmCarouselCentered]: !showArrows(
              variants,
              variantBreakpoints
            ),
          })}
          goToSlideTrigger={activeModelIdx}
          responsive={variantBreakpoints}
          partialVisible={
            (isTablet || isMobile) &&
            showArrows(variants, variantBreakpoints, 1)
          }
          removeArrowOnDeviceType={['mobile', 'tablet']}
          swipeable={isTablet || isMobile}
          arrows={
            !(isTablet || isMobile) && showArrows(variants, variantBreakpoints)
          }
        >
          {variants.map((v) => (
            <div
              key={v.pimIdentifier}
              className={cn(styles.crmVariant, {
                [styles.crmVariantActive]: v._selected,
                [styles.crmClickable]: canAddVariant() && !v._selected,
              })}
            >
              <div
                className={styles.crmClickableArea}
                onClick={() => {
                  if (canAddVariant() && !v._selected)
                    handleVariantClick(v.modelPimIdentifier, v.pimIdentifier);
                }}
              >
                <div className={styles.crmImage}>
                  <img
                    src={v.imageSrc}
                    style={{
                      aspectRatio: '16/9',
                      maxWidth: `${maxVariantImageWidth}px`,
                    }}
                    loading="lazy"
                  />
                  {v._selected && (
                    <SvgIcon type="check" size={1} color={`#${colorGreen}`} />
                  )}
                </div>
                <div className={styles.crmDetails}>
                  <p className={styles.crmName}>{v.name}</p>
                </div>
              </div>
              <CompareRangeModalRDP
                modelName={v?.name}
                variantId={v?.legacyId}
                pimIdentifier={v?.pimIdentifier}
                rdpDisclaimerOverride={(v as any)?.rdpDisclaimerOverride}
              />
            </div>
          ))}
        </CustomCarousel>
      </div>
    );
  };

  const removeDuplicates = React.useCallback(
    (items: CompareRangePopupVariant[]) => {
      const seenIDs = new Set<string | undefined>();
      const result: CompareRangePopupVariant[] = [];
      for (const item of items) {
        if (!seenIDs.has(item.pimIdentifier)) {
          seenIDs.add(item.pimIdentifier);
          result.push(item);
        }
      }

      return result;
    },
    []
  );

  const renderSelectedVariants = () => {
    let variants = [...compareRangePopupModels]
      .map((m) => m.variants.filter((v) => v._selected))
      .flat(); // get selected CompareRangePopupVariant[]

    variants = removeDuplicates(variants)
      .concat(
        Array(maxSelection)
          .fill(0)
          .map((__) => ({} as CompareRangePopupVariant)) // create maxSelection count of empty arrays
      ) // then merge those arrays
      .slice(0, maxSelection) // get the first maxSelection count from merged arrays
      .sort(
        (a, b) =>
          (a._order || Number.MAX_VALUE) - (b._order || Number.MAX_VALUE)
      );

    return (
      <div className={styles.crmCarouselContainer}>
        <CustomCarousel
          className={cn(
            styles.crmCarousel,
            styles.crmCarouselSelectedVariants,
            {
              [styles.crmCarouselCentered]: !showArrows(variants, breakpoints),
            }
          )}
          responsive={breakpoints}
          partialVisible={
            (isTablet || isMobile) && showArrows(variants, breakpoints, 1)
          }
          removeArrowOnDeviceType={['mobile', 'tablet']}
          swipeable={isTablet || isMobile}
          arrows={!(isTablet || isMobile) && showArrows(variants, breakpoints)}
        >
          {variants.map((v, idx) => (
            <div
              key={`${v.pimIdentifier}--${idx}`}
              className={cn(
                styles.crmVariant,
                styles.crmVariantActive,
                styles.crmVariantSelected
              )}
            >
              <div className={styles.crmImage}>
                {v.imageSrc && (
                  <img
                    src={v.imageSrc}
                    style={{
                      maxWidth: `${maxImageWidth}px`,
                    }}
                    loading="lazy"
                  />
                )}
              </div>
              <div className={styles.crmDetails}>
                <p className={styles.crmName}>{v.name}</p>
                {v._selected && (
                  <div className={styles.crmRemove}>
                    <Button
                      type="secondary"
                      buttonWidth="full-width"
                      buttonSize="small"
                      onClick={() =>
                        handleVariantClick(
                          v.modelPimIdentifier,
                          v.pimIdentifier
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </CustomCarousel>
      </div>
    );
  };

  return (
    <div className={cn(styles.crmContainer)}>
      <div ref={ref} className={styles.crmContent}>
        <h4 className={styles.crmHeader}>
          Select a model and variant below to compare:
        </h4>
        {compareRangePopupModels && renderModels()}
        {compareRangePopupModels.some((m) => m._active) && renderVariants()}
        {compareRangePopupModels.some((m) => m._active) &&
          renderSelectedVariants()}
        {showCTAs ? (
          compareRangePopupModels.some((m) => m._active) && (
            <div className={styles.crmCTAs}>
              <Button
                type="primary"
                text="Compare Now"
                onClick={handleCompareNow}
                disabled={!(getSelectedVariantsCount() > 1)}
              />
              <Button
                className={styles.crmClearAllCTA}
                type="text"
                text="Clear all"
                onClick={handleClearAll}
                disabled={!(getSelectedVariantsCount() > 0)}
              />
            </div>
          )
        ) : (
          <div className={styles.crmClearConfirm}>
            Are you sure you want to clear all?
            <Button
              type="text"
              text="Yes"
              buttonWidth="fit-text"
              onClick={handleConfirmClearAll}
            />
            /
            <Button
              type="text"
              text="No"
              buttonWidth="fit-text"
              onClick={handleClearAll}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const CompareRangeModal: React.FC<ModalProps> = ({ closeModal }) => {
  return (
    <Modal
      closeModal={closeModal}
      modalContentClass={styles.compareRangeModal}
      modalWrapperClass={styles.compareRangeModalWrapper}
      modalCloseButtonClass={styles.compareRangeModalClose}
      closeClickModal={false}
      lockScroll
    >
      <CompareRangeModalContent {...{ closeModal }} />
    </Modal>
  );
};
